/* GASSISA
=========================================================================================
* Descripción: Módulo de toma de inventarios - Encabezado
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 5/01/2026 | Lic. José Luis Aragón Cervantes  | Creación
 *      2      | 5/01/2026 | Lic. José Luis Aragón Cervantes  | Se agregan acciones de escaneo de código de barras y modo colector
 *      3      | 23/09/2026 | LIC. José Luis Aragón C.                       | Deshabilita autocomplete del lector
* ---------------------------------------------------------------------------------------
*/


import TextField from "../../ui/TextField";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Box, Divider, Snackbar, Alert, Typography, Paper, IconButton, Tooltip } from "@mui/material";

import FrmTomaInventarioHeader from "./FrmTomaInventarioHeader";
import MnuTomaInventarioActions from "./MnuTomaInventarioActions";
import SizeSoftXLayoutForm from "../../leyout/SizeSoftXLayoutForm";
import ModalBuscarArticulosSimple from "../../herramientas/ModalBuscarArticulosSimple";
import SizeSoftXLoadingProcess from "../../leyout/SizeSoftXLoadingProcess";
import SizeSoftXAlert from "../../utils/SizeSoftXAlert";
import SizeSoftXConfirmDialog from "../../leyout/SizeSoftXConfirmDialog";

import SizeSoftXTablePaginadoFilterServidor from "../../leyout/SizeSoftXTablePaginadoFilterServidor";
import ImportarConteosModal from "./ModalImportarConteos";
import ZonasDiferenciasModal from "./ModalZonasDiferencias";
import ConfirmDialog from "../ModalConfirmDialog";
import ModalEditarConteo3 from "./ModalEditarConteo3";
import SearchIcon from "@mui/icons-material/Search";
import ModalBuscarTomaInventario from "./ModalBuscarTomaInventario";
import SizeSoftXCodigoBarrasCamaraTomaInventario from "../../leyout/SizeSoftXCodigoBarrasCamaraTomaInventario";

import { useInventarios } from "../../../context/Inventarios/InventariosContext";
import { useSesion } from "../../../context/System/SesionContext";
import { useFormTabLock } from "../../../hooks/useFormTabLock";
import { useLayout } from "../../../context/System/LayoutContext";

const emptyHeader = {
  folioinventario: 0,
  usuarioresponsable: "",
  totalzonas: 0,
  totalarticulos: 0,
  totalinventariados: 0,
  totalfaltantes: 0,
  articuloscondiferencias: 0,
  statusinventario: "PE",
  almacenexhibidor: "",
  tipoinventario: "GENERAL",
};

export default function FrmTomaInventario({ folioInicial }) {
  const { isLocked: isFormLocked } = useFormTabLock('toma_inventario', {
    title: 'Toma Inventario',
  });

  const { handleOpenAlert } = useLayout();

  const [loading, setLoading] = useState(false);

  // UI state
  const [openImportar, setOpenImportar] = useState(false);
  const [zonasDiff, setZonasDiff] = useState([]);
  const [openZonasDiff, setOpenZonasDiff] = useState(false);
  const [confirmFinalizar, setConfirmFinalizar] = useState(false);
  const [confirmCancelarToma, setConfirmCancelarToma] = useState(false);
  const [confirmEliminarUno, setConfirmEliminarUno] = useState(false);
  const [openEditarConteo3, setOpenEditarConteo3] = useState(false);
  const [openBuscarToma, setOpenBuscarToma] = useState(false);
  const [confirmEliminarTodos, setConfirmEliminarTodos] = useState(false);
  const [toast, setToast] = useState(null);
  const [statusOpenDialog, setStatusOpenDialog] = useState(false);

  const [titleOpenDialog, setTitleOpenDialog] = useState("");
  const [messageOpenDialog, setMessageOpenDialog] = useState("");
  const [statusWaiting, setStatusWaiting] = useState(false);

  const [rowSelected, setRowSelected] = useState(null);
  const barcodeInputRef = useRef(null);
  const barcodeQueueRef = useRef(Promise.resolve());
  const [colectorMode, setColectorMode] = useState(false);
  const [codigosEscaneados, setCodigosEscaneados] = useState([]);

  const { parametrosCajaForm, usuarioData } = useSesion();

  const {
    statusModalBuscadorArticulosTomaInventario,
    // estado central (si ya lo manejas en contexto)
    tomaHeader,
    setTomaHeader,
    tomaDetalle,
    setTomaDetalle,

    // Paginación detalle
    tomaDetallePage,
    tomaDetallePageSize,
    tomaDetalleTotalCount,
    tomaDetalleFilter,
    handleTomaChangePage,
    handleTomaChangePageSize,
    handleTomaFilter,

    handleTomaActualizarConteoAFijar,
    handleTomaEliminarArticulo,
    handleTomaEliminarTodo,

    // handlers del contexto (los buenos)
    handleTomaReset,
    handleTomaCargarActivo,
    handleTomaLoadAll,
    handleTomaCrearFolio,
    handleTomaGuardarHeader,
    handleTomaCargarArticulos,
    handleTomaGuardarDetalle,
    handleTomaImportarConteos,
    handleTomaValidarZonas,
    handleTomaFijarConteoPorRegla,
    handleTomaFijarTodoConteo,
    handleTomaReiniciarConteos,
    handleTomaCerrarInventario,
    handleTomaAplicarInventario,
    handleTomaCancelarInventario,
    handleTomaImprimirPdf,
    handleTomaBuscarYAgregarPorCodigoBarras,
    handleCloseBuscarArticuloDescripcionTomaInventario,
    handleArticuloSeleccionado,
    listaAlmacenesExhibidoresGeneral,
    handleObtenerAlmacenesExhibidores,
    handleClickRegresarMainDashboard,
  } = useInventarios();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isFormLocked) {
      alert("El formulario de Toma Inventario ya está abierto en otra pestaña del navegador.\n\nPor favor, cierre la otra ventana o pestaña para continuar.");
      setTimeout(() => {
        if(handleClickRegresarMainDashboard) handleClickRegresarMainDashboard();
      }, 900);
    }
  }, [isFormLocked]);

  // Fuente de verdad: si contexto no trae header aún, usa empty
  const header = tomaHeader || emptyHeader;
  const rows = Array.isArray(tomaDetalle) ? tomaDetalle : [];

  const canEdit = useMemo(
    () => header.statusinventario === "AB",
    [header.statusinventario]
  );
  const hasFolio = useMemo(
    () => (header.folioinventario || 0) > 0,
    [header.folioinventario]
  );
  const tieneArticulos = useMemo(
    () => Array.isArray(tomaDetalle) && tomaDetalle.length > 0,
    [tomaDetalle]
  );

  // --------- Cargar inicial ----------
  useEffect(() => {
    (async () => {
      // NOTA: el token tenant es JWE (cifrado, 5 partes base64url): no se
      // puede inspeccionar con atob en el browser. Si hay 401, el backend
      // lo reporta en su log y el RequestContext lo notifica.
      setLoading(true);
      try {
        if (folioInicial && folioInicial > 0) {
          // Si hay folio inicial, cargarlo directamente
          await handleTomaLoadAll(folioInicial);
        } else {
          // Si no hay folio, buscar inventario activo
          const tieneActivo = await handleTomaCargarActivo();
          if (!tieneActivo) {
            // No hay activo, asegurar estado limpio
            if (!tomaHeader)
              setTomaHeader({
                ...emptyHeader,
                usuarioresponsable: usuarioData?.Nombre || "",
              });
            if (!tomaDetalle) setTomaDetalle([]);
          }
        }
      } catch {
        setToast({ type: "error", msg: "Error al cargar inventario." });
      } finally {
        setLoading(false);
      }
    })();

    // Cargar lista de almacenes si esté vacía
    if (!listaAlmacenesExhibidoresGeneral || listaAlmacenesExhibidoresGeneral.length === 0) {
      handleObtenerAlmacenesExhibidores("GENERAL");
    }
  }, [folioInicial]);

  // Establecer usuario logueado como responsable cuando esté disponible
  useEffect(() => {
    if (usuarioData?.Nombre && tomaHeader && !tomaHeader.usuarioresponsable) {
      setTomaHeader((prev) => ({
        ...prev,
        usuarioresponsable: usuarioData.Nombre,
      }));
    }
  }, [usuarioData]);

  useEffect(() => {
    if (hasFolio && !loading) {
      barcodeInputRef.current?.focus();
    }
  }, [hasFolio, loading]);

  // --------- Acciones (sin pisar nombres) ----------
  const actCrearFolio = async () => {
    const esInventarioGeneral =
      (header.tipoinventario || "GENERAL").trim().toUpperCase() === "GENERAL";

    setLoading(true);
    if (esInventarioGeneral) {
      
      setStatusWaiting(true);
    }
    try {
      
      const h = await handleTomaCrearFolio();
      
      if (!h) {
        handleOpenAlert("El proceso terminó sin devolver un encabezado creado","error");
        return;
      }
      setToast({
        type: "success",
        msg: `Folio iniciado: ${h.folioinventario || h.FolioInventario}`,
      });
    } catch (error) {
      handleOpenAlert("Error en la acción Iniciar", error);
      setToast({ type: "error", msg: "No se pudo iniciar el folio." });
    } finally {
      setLoading(false);
      if (esInventarioGeneral) {
        setStatusWaiting(false);
      }
    }
  };

  const actGuardarCabecera = async () => {
    setLoading(true);
    try {
      await handleTomaGuardarHeader(header);
      setToast({ type: "success", msg: "Cabecera guardada." });
    } catch {
      setToast({ type: "error", msg: "Error al guardar cabecera." });
    } finally {
      setLoading(false);
    }
  };

  const actRefrescar = async () => {
    setLoading(true);
    try {
      if (hasFolio) {
        await handleTomaLoadAll(header.folioinventario);
      } else {
        const tieneActivo = await handleTomaCargarActivo();
        if (!tieneActivo) {
          setTomaHeader({
            ...emptyHeader,
            usuarioresponsable: usuarioData?.Nombre || "",
          });
          setTomaDetalle([]);
        }
      }
      setToast({ type: "success", msg: "Toma de inventario actualizada." });
    } catch {
      setToast({ type: "error", msg: "No se pudo refrescar la toma de inventario." });
    } finally {
      setLoading(false);
    }
  };

  const actRecargarActivo = async () => {
    setLoading(true);
    try {
      const tieneActivo = await handleTomaCargarActivo();
      setToast({
        type: tieneActivo ? "success" : "info",
        msg: tieneActivo
          ? "Inventario activo cargado."
          : "No hay un inventario activo en este momento.",
      });
    } catch {
      setToast({ type: "error", msg: "No se pudo cargar el inventario activo." });
    } finally {
      setLoading(false);
    }
  };

  const actCargarArticulos = async () => {
    if (!hasFolio)
      return setToast({ type: "info", msg: "Primero inicia el folio." });
    if (!String(header.almacenexhibidor || "").trim())
      return setToast({ type: "info", msg: "Selecciona Almacén/Exhibidor." });

    setLoading(true);
    setStatusWaiting(true);
    try {
      await handleTomaCargarArticulos();
    } catch {
      setToast({ type: "error", msg: "No se pudo cargar artículos." });
    } finally {
      setLoading(false);
      setStatusWaiting(false);
    }
  };

  const actImportarConteos = async () => {
    if (!hasFolio)
      return setToast({ type: "info", msg: "Primero inicia el folio." });
    if (!String(header.almacenexhibidor || "").trim())
      return setToast({ type: "info", msg: "Selecciona Almacén/Exhibidor." });
    setOpenImportar(true);
  };

  const actValidarZonas = async () => {
    if (!hasFolio) return;
    setLoading(true);
    setStatusWaiting(true);
    try {
      const diffs = await handleTomaValidarZonas(header.folioinventario);
      setZonasDiff(diffs || []);
      setOpenZonasDiff(true);

      const hayDif = (diffs || []).some((x) => x?.Diferentes);
      setToast({
        type: hayDif ? "info" : "success",
        msg: hayDif
          ? "Hay zonas con diferencias. Toca Conteo 3 en esas zonas."
          : "Conteo 1 y 2 consistentes por zonas.",
      });
    } catch {
      setToast({ type: "error", msg: "Error al validar zonas." });
    } finally {
      setLoading(false);
      setStatusWaiting(false);
    }
  };

  const actFijarConteo = async () => {
    if (!hasFolio) return;
    setLoading(true);
    setStatusWaiting(true);
    try {
      await handleTomaFijarConteoPorRegla(header.folioinventario);
      await handleTomaLoadAll(header.folioinventario);
      setToast({ type: "success", msg: "Conteo a aplicar fijado por regla." });
    } catch {
      setToast({ type: "error", msg: "No se pudo fijar conteo." });
    } finally {
      setLoading(false);
      setStatusWaiting(false);
    }
  };

  const actFijarTodo1 = async () => {
    if (!hasFolio) return;
    setLoading(true);
    setStatusWaiting(true);
    try {
      await handleTomaFijarTodoConteo(1);
      await handleTomaLoadAll(header.folioinventario);
      setToast({ type: "success", msg: "Todos fijados a conteo 1." });
    } catch {
      setToast({ type: "error", msg: "No se pudo fijar todo a conteo 1." });
    } finally {
      setLoading(false);
      setStatusWaiting(false);
    }
  };

  const actFijarTodo2 = async () => {
    if (!hasFolio) return;
    setLoading(true);
    setStatusWaiting(true);
    try {
      await handleTomaFijarTodoConteo(2);
      await handleTomaLoadAll(header.folioinventario);
      setToast({ type: "success", msg: "Todos fijados a conteo 2." });
    } catch {
      setToast({ type: "error", msg: "No se pudo fijar todo a conteo 2." });
    } finally {
      setLoading(false);
      setStatusWaiting(false);
    }
  };

  const actFijarTodo3 = async () => {
    if (!hasFolio) return;
    setLoading(true);
    setStatusWaiting(true);
    try {
      await handleTomaFijarTodoConteo(3);
      await handleTomaLoadAll(header.folioinventario);
      setToast({ type: "success", msg: "Todos fijados a conteo 3." });
    } catch {
      setToast({ type: "error", msg: "No se pudo fijar todo a conteo 3." });
    } finally {
      setLoading(false);
      setStatusWaiting(false);
    }
  };

  const actReiniciarConteos = async () => {
    if (!hasFolio) return;
    setLoading(true);
    setStatusWaiting(true);
    try {
      await handleTomaReiniciarConteos();
      await handleTomaLoadAll(header.folioinventario);
      setToast({ type: "success", msg: "Conteos reiniciados." });
    } catch {
      setToast({ type: "error", msg: "No se pudieron reiniciar los conteos." });
    } finally {
      setLoading(false);
      setStatusWaiting(false);
    }
  };

  const actGuardarDetalle = async () => {
    if (!hasFolio) return;
    setLoading(true);
    try {
      await handleTomaGuardarDetalle(header.folioinventario, rows);
      setToast({ type: "success", msg: "Detalle guardado." });
    } catch {
      setToast({ type: "error", msg: "Error al guardar detalle." });
    } finally {
      setLoading(false);
    }
  };

  const actAplicarCerrar = async () => {
    setLoading(true);
    setStatusWaiting(true);
    try {
      if (header.statusinventario === "AB") {
        const cerrado = await handleTomaCerrarInventario(
          header.folioinventario
        );
        if (!cerrado) throw new Error("No se pudo cerrar el inventario");
      }

      const aplicado = await handleTomaAplicarInventario(
        header.folioinventario,
        true
      );
      if (!aplicado) throw new Error("No se pudo aplicar el inventario");

      await handleTomaLoadAll(header.folioinventario);
      setToast({
        type: "success",
        msg: "Inventario aplicado al almacén/exhibidor.",
      });
    } catch {
      setToast({ type: "error", msg: "No se pudo aplicar el inventario." });
    } finally {
      setLoading(false);
      setStatusWaiting(false);
      setConfirmFinalizar(false);
    }
  };

  const actCancelarToma = async () => {
    if (!hasFolio) return;
    setLoading(true);
    setStatusWaiting(true);
    try {
      const cancelado = await handleTomaCancelarInventario(header.folioinventario);
      if (!cancelado) throw new Error("No se pudo cancelar la toma");
      setRowSelected(null);
      setConteosImportados(null);
      setToast({ type: "success", msg: "Toma cancelada. Puedes iniciar otra toma." });
    } catch (e) {
      setToast({ type: "error", msg: e?.message || "No se pudo cancelar la toma." });
    } finally {
      setLoading(false);
      setStatusWaiting(false);
      setConfirmCancelarToma(false);
    }
  };

  const actImprimir = async () => {
    if (!hasFolio) return;
    setLoading(true);
    await handleTomaImprimirPdf(header.folioinventario);
    setLoading(false);
  };

  const actBuscarToma = async (row) => {
    if (!row?.folioinventario) return;
    setLoading(true);
    try {
      await handleTomaLoadAll(row.folioinventario);
    } catch {
      setToast({ type: "error", msg: "Error al cargar el inventario." });
    } finally {
      setLoading(false);
    }
  };

  const procesarCodigoBarras = async (codigo) => {
    setLoading(true);
    try {
      const result = await handleTomaBuscarYAgregarPorCodigoBarras(codigo);
      if (result?.HasError) {
        setToast({ type: "error", msg: result.Message || "Error al buscar artículo" });
      } else {
        setToast({ type: "success", msg: result.Message || "Artículo agregado" });
        await handleTomaLoadAll(header.folioinventario);
      }
    } catch {
      setToast({ type: "error", msg: "Error al buscar código de barras" });
    } finally {
      setLoading(false);
      barcodeInputRef.current?.focus();
    }
  };

  const actBuscarYBarcode = () => {
    const codigo = barcodeInputRef.current?.value?.trim() || "";
    if (!hasFolio || !codigo) return;

    barcodeInputRef.current.value = "";

    if (colectorMode) {
      setCodigosEscaneados((prev) => {
        if (prev.includes(codigo)) return prev;
        return [...prev, codigo];
      });
      barcodeInputRef.current?.focus();
      return;
    }

    barcodeQueueRef.current = barcodeQueueRef.current
      .then(() => procesarCodigoBarras(codigo))
      .catch(() => undefined);
  };

  const actAplicarCodigosColector = async () => {
    if (!codigosEscaneados.length) return;
    setLoading(true);
    let errors = 0;
    let success = 0;
    for (const codigo of codigosEscaneados) {
      try {
        const result = await handleTomaBuscarYAgregarPorCodigoBarras(codigo);
        if (result?.HasError) {
          errors++;
        } else {
          success++;
        }
      } catch {
        errors++;
      }
    }
    setCodigosEscaneados([]);
    setLoading(false);
    await handleTomaLoadAll(header.folioinventario);
    setToast({
      type: errors > 0 ? "warning" : "success",
      msg: `${success} artículo(s) agregado(s)` + (errors > 0 ? `, ${errors} error(es)` : ""),
    });
    barcodeInputRef.current?.focus();
  };

  // Eliminar artículo seleccionado
  const actEliminarArticulo = async () => {
    if (!rowSelected || !rowSelected.clavearticulo) return;
    try {
      await handleTomaEliminarArticulo(
        header.folioinventario,
        rowSelected.clavearticulo
      );
      setToast({ type: "success", msg: "Artículo eliminado" });
      await handleTomaLoadAll(header.folioinventario);
      setRowSelected(null);
    } catch (e) {
      setToast({ type: "error", msg: e?.message || "Error al eliminar" });
    } finally {
      setLoading(false);
      setConfirmEliminarUno(false);
    }
  };

  // Eliminar todos los artículos
  const actEliminarTodos = async () => {
    try {
      await handleTomaEliminarTodo(header.folioinventario);
      setToast({ type: "success", msg: "Todos los artículos eliminados" });
      await handleTomaLoadAll(header.folioinventario);
    } catch (e) {
      setToast({ type: "error", msg: e?.message || "Error al eliminar" });
    } finally {
      setLoading(false);
      setConfirmEliminarTodos(false);
    }
  };

  // Editar ConteoAFijar localmente en rows y guardar en BD inmediatamente
  const onChangeConteoAFijar = async (folio, clave, medida, next) => {
    // Actualizar estado local inmediatamente
    setTomaDetalle((prev) =>
      (prev || []).map((r) =>
        r.folioinventario === folio &&
        r.clavearticulo === clave &&
        Number(r.medida) === Number(medida)
          ? { ...r, conteoafijar: next }
          : r
      )
    );

    // Guardar en BD inmediatamente
    const valor = parseInt(next) || 0;
    if (valor >= 1 && valor <= 3) {
      await handleTomaActualizarConteoAFijar(
        folio,
        clave,
        Number(medida),
        valor
      );
    }
  };

  // --------- Tabla (sin DataGrid) ----------
  const columns = useMemo(() => {
    // Nombres de campos del API: zona, clavearticulo, descripcion, medida, conteo1, conteo2, conteo3, conteoafijar, diferencia, status
    return [
      {
        field: "__rowIndex",
        title: "#",
        width: 50,
        hideSort: true,
        align: "center",
      },
      { field: "zona", title: "Zona", width: 90, hideSort: false },
      { field: "clavearticulo", title: "Clave", width: 120, hideSort: false },
      {
        field: "descripcion",
        title: "Descripción",
        width: 200,
        hideSort: false,
      },
      {
        field: "medida",
        title: "Med",
        width: 60,
        hideSort: false,
        align: "center",
      },
      {
        field: "existencia",
        title: "Exist",
        width: 70,
        hideSort: false,
        align: "center",
      },

      {
        field: "conteo1",
        title: "C1",
        width: 60,
        hideSort: false,
        align: "center",
      },
      {
        field: "conteo2",
        title: "C2",
        width: 60,
        hideSort: false,
        align: "center",
      },
      {
        field: "conteo3",
        title: "C3",
        width: 60,
        hideSort: false,
        align: "center",
      },

      {
        field: "conteoafijar",
        title: "A fijar",
        hideSort: false,
        align: "center",
        render: (r) => (
          <TextField autoComplete="off"
            size="small"
            type="text"
            inputMode="numeric"
            enterKeyHint="enter"
            value={r?.conteoafijar ?? 0}
            disabled={!canEdit || (r?.conteoafijar ?? 0) > 0}
            inputProps={{ style: { textAlign: "center", width: 90 } }}
            onChange={(e) =>
              onChangeConteoAFijar(
                r.folioinventario,
                r.clavearticulo,
                r.medida,
                e.target.value
              )
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onChangeConteoAFijar(
                  r.folioinventario,
                  r.clavearticulo,
                  r.medida,
                  e.target.value
                );
                e.target.blur();
              }
            }}
          />
        ),
      },

      {
        field: "diferencia",
        title: "Diferencia",
        hideSort: false,
        align: "center",
        render: (r) => {
          const dif = Math.abs(Number(r?.diferencia ?? 0));
          return (
            <Typography
              sx={{
                fontWeight: 800,
                color: dif !== 0 ? "error.main" : "success.main",
              }}
            >
              {dif}
            </Typography>
          );
        },
      },
    ];
  }, [canEdit]);

  const conteosImportados = useMemo(() => {
    const result = [];
    if (rows.some((r) => Number(r.conteo1 ?? 0) > 0)) result.push(1);
    if (rows.some((r) => Number(r.conteo2 ?? 0) > 0)) result.push(2);
    if (rows.some((r) => Number(r.conteo3 ?? 0) > 0)) result.push(3);
    return result;
  }, [rows]);

  const autoMatchedKeys = useMemo(
    () =>
      (rows || [])
        .filter((r) => {
          const af = Number(r.conteoafijar ?? 0);
          if (af === 1)
            return Math.abs(Number(r.existencia ?? 0) - Number(r.conteo1 ?? 0)) < 0.001;
          if (af === 2)
            return Math.abs(Number(r.existencia ?? 0) - Number(r.conteo2 ?? 0)) < 0.001;
          if (af === 3)
            return Math.abs(Number(r.existencia ?? 0) - Number(r.conteo3 ?? 0)) < 0.001;
          return false;
        })
        .map((r) => `${r.clavearticulo}|${r.medida}`),
    [rows]
  );

  const autoMatchedSet = useMemo(() => new Set(autoMatchedKeys), [autoMatchedKeys]);

  // ID: usa uno estable
  const getRowIdField = "RowKey";
  const dataForTable = useMemo(() => {
    return rows.map((r, idx) => ({
      ...r,
      RowKey:
        r.RowKey ||
        `${r.folioinventario}-${r.clavearticulo}-${r.medida}-${idx}`,
    }));
  }, [rows]);

  return (
    <Fragment>
      <SizeSoftXLayoutForm
        titlePage="Módulo de toma de inventarios"
        buttonActions={
          <Fragment>
            <Tooltip title="Buscar toma de inventario">
              <IconButton onClick={() => setOpenBuscarToma(true)}>
                <SearchIcon className="icon" />
                <p className="icon">Buscar</p>
              </IconButton>
            </Tooltip>
            <MnuTomaInventarioActions
            onAccion={(action) => {
              switch (action) {
                case "crearFolio":
                  actCrearFolio();
                  break;
                case "guardar":
                  actGuardarCabecera();
                  break;
                case "refrescar":
                  actRefrescar();
                  break;
                case "recargarActivo":
                  actRecargarActivo();
                  break;
                case "cargarArticulos":
                  actCargarArticulos();
                  break;
                case "buscarArticulo":
                  break;
                case "importar":
                  actImportarConteos();
                  break;
                case "validar":
                  actValidarZonas();
                  break;
                case "fijarTodo1":
                  actFijarTodo1();
                  break;
                case "fijarTodo2":
                  actFijarTodo2();
                  break;
                case "fijarTodo3":
                  actFijarTodo3();
                  break;
                case "reiniciar":
                  actReiniciarConteos();
                  break;
                case "aplicarCerrar":
                  setConfirmFinalizar(true);
                  break;
                case "cancelarToma":
                  setConfirmCancelarToma(true);
                  break;
                case "imprimir":
                  actImprimir();
                  break;
              }
            }}
            tieneArticulos={tieneArticulos}
            rowSelected={rowSelected}
            onEliminarArticulo={() => setConfirmEliminarUno(true)}
            onEliminarTodos={() => setConfirmEliminarTodos(true)}
            onEditarConteo3={() => setOpenEditarConteo3(true)}
          />
          </Fragment>
        }
      >
        <Box sx={{ p: 2 }}>
          <FrmTomaInventarioHeader
            value={header}
            onChange={setTomaHeader}
            loading={loading}
            listaAlmacenesExhibidores={listaAlmacenesExhibidoresGeneral}
          />
          <Divider sx={{ my: 2 }} />
          <Box
            className="framelectorcodigosbarras"
            sx={{
              display: "flex",
              gap: 1,
              mb: 1,
              alignItems: "center",
              flexWrap: "wrap",
              "& .camara-codigo-barras-portatil": {
                flex: "0 0 auto",
                m: 0,
              },
              "& .camara-codigo-barras-portatil__toggle": {
                width: "auto",
                height: 34,
                px: 1,
                fontSize: "0.78rem",
                whiteSpace: "nowrap",
              },
            }}
          >
            <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: 12, whiteSpace: "nowrap" }}>
              <input
                type="checkbox"
                checked={colectorMode}
                onChange={(e) => setColectorMode(e.target.checked)}
                disabled={!hasFolio || loading}
              />
              Colector
            </label>
            <input
              ref={barcodeInputRef}
              type="text"
              defaultValue=""
              autoComplete="off"
              name="lector-codigo-barras"
              aria-autocomplete="none"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                actBuscarYBarcode();
              }}
              placeholder="Escanear código de barras aquí"
              disabled={!hasFolio}
              style={{
                width: 280,
                padding: "8px 12px",
                fontSize: 14,
                color: "#fff",
                backgroundColor: "#000",
                border: "1px solid #555",
                borderRadius: 4,
                outline: "none",
                textAlign: "center",
                letterSpacing: 1,
              }}
            />
            <SizeSoftXCodigoBarrasCamaraTomaInventario
              onScan={async (codigo) => {
                barcodeInputRef.current.value = "";
                await procesarCodigoBarras(codigo);
                barcodeInputRef.current?.focus();
              }}
            />
          </Box>
          {colectorMode && codigosEscaneados.length > 0 && (
            <Box sx={{ mb: 1, p: 1, border: "1px solid #ccc", borderRadius: 1, maxHeight: 200, overflow: "auto" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                  Códigos escaneados ({codigosEscaneados.length})
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <button
                    onClick={() => setCodigosEscaneados([])}
                    style={{ fontSize: 11, padding: "2px 6px", cursor: "pointer" }}
                    disabled={loading}
                  >
                    Limpiar
                  </button>
                  <button
                    onClick={actAplicarCodigosColector}
                    style={{ fontSize: 11, padding: "2px 6px", cursor: "pointer", fontWeight: "bold" }}
                    disabled={loading}
                  >
                    Aplicar ({codigosEscaneados.length})
                  </button>
                </Box>
              </Box>
              {codigosEscaneados.map((cod, idx) => (
                <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.25 }}>
                  <Typography variant="caption" sx={{ fontFamily: "monospace" }}>{cod}</Typography>
                  <button
                    onClick={() => setCodigosEscaneados((prev) => prev.filter((_, i) => i !== idx))}
                    style={{ fontSize: 10, padding: "0 4px", cursor: "pointer", color: "red", border: "none", background: "none" }}
                    disabled={loading}
                  >
                    ✕
                  </button>
                </Box>
              ))}
            </Box>
          )}
          <Paper sx={{ p: 1 }}>
            <SizeSoftXTablePaginadoFilterServidor
              data={dataForTable}
              columns={columns}
              totalDeRegistros={tomaDetalleTotalCount}
              id={getRowIdField}
              row={rowSelected}
              setRow={setRowSelected}
              defaultRow={null}
              rowsPerPageSet={tomaDetallePageSize}
              disableStickyHeader={false}
              fontSize="small"
              rowSpacing={6}
              EspaciadorColumnas={10}
              pageNow={tomaDetallePage - 1}
              initialFilter={tomaDetalleFilter}
              onPageChangeExt={handleTomaChangePage}
              onRowsPerPageChangeExt={handleTomaChangePageSize}
              onFilterExt={handleTomaFilter}
              getRowStyle={(r) =>
                autoMatchedSet.has(
                  `${r.clavearticulo}|${r.medida}`
                )
                  ? { backgroundColor: "#fff176" }
                  : {}
              }
            />
          </Paper>
          <ImportarConteosModal
            open={openImportar}
            folioInventario={header.folioinventario}
            almacenExhibidor={header.almacenexhibidor}
            conteosImportados={conteosImportados}
            onClose={() => setOpenImportar(false)}
            onImported={() => handleTomaLoadAll(header.folioinventario)}
          />
          <ZonasDiferenciasModal
            open={openZonasDiff}
            diffs={zonasDiff}
            onClose={() => setOpenZonasDiff(false)}
          />
          <ConfirmDialog
            open={confirmCancelarToma}
            title="Cancelar toma de inventarios"
            message="Se eliminará la toma actual y se liberará el folio sin incrementar el consecutivo. No se aplicarán ajustes al almacén. ¿Continuar?"
            confirmText="Cancelar toma"
            danger
            loading={loading}
            onClose={() => setConfirmCancelarToma(false)}
            onConfirm={actCancelarToma}
          />
          <ConfirmDialog
            open={confirmFinalizar}
            title="Aplicar inventario"
            message="Se cerrará el inventario y se aplicarán los ajustes al almacén/exhibidor. Esta acción no se puede deshacer. ¿Continuar?"
            confirmText="Aplicar"
            danger
            loading={loading}
            onClose={() => setConfirmFinalizar(false)}
            onConfirm={actAplicarCerrar}
          />
          <ConfirmDialog
            open={confirmEliminarUno}
            title="Eliminar artículo"
            message={`¿Eliminar el artículo "${rowSelected?.clavearticulo}" con todas sus medidas?`}
            confirmText="Eliminar"
            danger
            loading={loading}
            onClose={() => setConfirmEliminarUno(false)}
            onConfirm={actEliminarArticulo}
          />
          <ConfirmDialog
            open={confirmEliminarTodos}
            title="Eliminar todos los artículos"
            message="¿Eliminar TODOS los artículos del inventario actual? Esta acción no se puede deshacer."
            confirmText="Eliminar todo"
            danger
            loading={loading}
            onClose={() => setConfirmEliminarTodos(false)}
            onConfirm={actEliminarTodos}
          />
          <Snackbar
            open={!!toast}
            autoHideDuration={3500}
            onClose={() => setToast(null)}
          >
            {toast ? (
              <Alert severity={toast.type} onClose={() => setToast(null)}>
                {toast.msg}
              </Alert>
            ) : null}
          </Snackbar>
        </Box>
      </SizeSoftXLayoutForm>
      <ModalBuscarArticulosSimple
        openModal={statusModalBuscadorArticulosTomaInventario}
        handleCloseModal={handleCloseBuscarArticuloDescripcionTomaInventario}
        dominio={parametrosCajaForm.dominio}
        handleArticuloSelect={handleArticuloSeleccionado}
      />
      <SizeSoftXConfirmDialog
        open={statusOpenDialog}
        title={titleOpenDialog}
        message={messageOpenDialog}
        handleConfirmDialog={() => handleEliminarData(titleOpenDialog)}
      />
      <SizeSoftXAlert />
      <SizeSoftXLoadingProcess
        open={statusWaiting}
        message="Procesando información, por favor espere..."
      />
      <ModalEditarConteo3
        open={openEditarConteo3}
        row={rowSelected}
        onClose={() => { setOpenEditarConteo3(false); barcodeInputRef.current?.focus(); }}
      />
      <ModalBuscarTomaInventario
        openModal={openBuscarToma}
        handleCloseModal={() => setOpenBuscarToma(false)}
        onSelectToma={actBuscarToma}
      />
    </Fragment>
  );
}
